import Button from 'components/ui/Button/Button';
import FormGroupSelect from 'components/ui/FormGroupSelect/FormGroupSelect';
import RadioGroup from 'components/ui/RadioGroup/RadioGroup';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { savePreferredHotel } from 'services/hotel';
import { showError, showSuccess } from 'utils/utils';
import { Hotel } from 'ts/hotel/interfaces/HotelResponse';
import ReactCountryFlag from 'react-country-flag';
import { createPortal } from 'react-dom';
import MaximizeModal from 'components/shared/MaximizeModal';
import ScreenshotSupport from 'components/shared/ScreenshotSupport';
import TextTags from 'components/shared/ui/TextTags';
import useDraggableModal from 'hooks/useDraggableModal';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface PreferredHotelModalProps {
  onClose: () => void;
  hotel: Hotel;
}

const PreferredHotelModal: FC<PreferredHotelModalProps> = ({ onClose, hotel }) => {
  const [t] = useTranslation();
  const [recommended, setRecommended] = useState('1');
  const [recommendedPositive, setRecommendedPositive] = useState('');
  const [recommendedNegative, setRecommendedNegative] = useState('');
  const [languageCode, setLanguageCode] = useState('en');
  const [loading, setLoading] = useState(false);
  const sanitizeUrlPattern = /[^a-zA-Z0-9-_. \u0590-\u05FF]/g;
  const [positiveTags, setPositiveTags] = useState<string[]>([]);
  const [negativeTags, setNegativeTags] = useState<string[]>([]);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const isRemove = recommended === '3';

  const options = [
    {
      label: t('hotel.preffered'),
      value: '1',
    },
    {
      label: t('hotel.excluded'),
      value: '2',
    },
    {
      label: t('hotel.remove_preffered'),
      value: '3',
    },
  ];

  const langOptions = [
    {
      value: 'en',
      label: 'English',
      html: (
        <Fragment>
          <ReactCountryFlag svg countryCode={'us'} className="mr-5" />
          English
        </Fragment>
      ),
    },
    {
      value: 'he',
      label: 'Hebrew',
      html: (
        <Fragment>
          <ReactCountryFlag svg countryCode={'il'} className="mr-5" />
          Hebrew
        </Fragment>
      ),
    },
  ];

  const onClickSave = async () => {
    setLoading(true);

    const params = {
      hotelItemBPID: hotel.item.bpid,
      hotelItemName: hotel.item.name,
      hotelAddressInfoAddress: hotel.addressInfo.address,
      hotelAddressInfoPhone: hotel.addressInfo.phone || '',
      hotelAddressInfoCountry: hotel.addressInfo.country,
      hotelItemSysSuppCode: hotel.item.sysSuppCode,
      hotelItemCode: hotel.itemCode,
      searchToken: sessionStorage.getItem('searchTokenHotels') || '',
      recommended: !isRemove && recommended === '1',
      recommendedPositive,
      recommendedNegative,
      languageCode,
      negativeTags,
      positiveTags,
      ...(isRemove ? { removeRecommended: true } : {}),
    };

    const result = await savePreferredHotel(params);

    if (!result || result?.hasErrors || !result?.success) {
      showError(t('something_went_wrong'));
      console.error(result?.errorMessage);
    } else if (result?.success) {
      showSuccess(t('hotel.preferred_saved'));
      onClose();
    }

    setLoading(false);
  };

  return createPortal(
    <div
      ref={modalOverlayRef}
      className="modal-overlay modal-overlay-top modal-maximizable"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={modalRef} className={'modal modal-sm active'} style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <div className="mb-10">
            <h5 className="text-bold text-md text-uppercase mb-5">{hotel.item.name}</h5>
            <div className="text-light">{hotel.addressInfo.address}</div>
          </div>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => onClose()} />
          </div>
        </div>
        <div className="modal-content">
          <RadioGroup options={options} value={recommended} onChange={setRecommended} />

          <FormGroupSelect
            label={t('hotel.language')}
            options={langOptions}
            onChange={(val) => setLanguageCode(val)}
            value={languageCode}
          />
          <div className="form-group">
            <label className="text-success">{t('hotel.positive_reviews')}</label>
            <textarea
              className="form-control form-control-textarea"
              style={{ height: 85 }}
              value={recommendedPositive}
              onChange={(e) =>
                setRecommendedPositive(e.target.value.replace(sanitizeUrlPattern, ''))
              }
            />
          </div>
          <TextTags
            labelClassName="text-success"
            tags={positiveTags}
            onChange={setPositiveTags}
            label={t('hotel.positive_reviews_tags')}
          />
          <div className="form-group">
            <label className="color-danger">{t('hotel.negative_reviews')}</label>
            <textarea
              className="form-control form-control-textarea"
              style={{ height: 85 }}
              value={recommendedNegative}
              onChange={(e) =>
                setRecommendedNegative(e.target.value.replace(sanitizeUrlPattern, ''))
              }
            />
          </div>
          <TextTags
            labelClassName="color-danger"
            tags={negativeTags}
            onChange={setNegativeTags}
            label={t('hotel.negative_reviews_tags')}
          />
        </div>

        <div className="modal-footer">
          <Button
            title={t('save')}
            className="btn btn-primary ml-0 mr-a"
            onClick={onClickSave}
            disabled={loading}
          />
          <Button title={t('close')} className="btn btn-default" onClick={() => onClose()} />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default withErrorBoundary(PreferredHotelModal, 'PreferredHotelModal');
